import { createContext, useContext, useEffect, useState } from "react";
import { auth as authApi, admin as adminApi, tokenStore } from "@/lib/api";
import { setActiveScope } from "@/lib/cache";
import { getCurrentSubscription, subscribePush } from "@/lib/push";

const AuthCtx = createContext(null);

/**
 * Lightweight synchronous hint of whether the user was authed last time we
 * resolved /auth/me. Lives in localStorage so LandingRoute can pick it up
 * BEFORE the async `refresh()` round-trip finishes — that's what stops the
 * marketing page from flashing on the dashboard's first paint.
 *
 * It is intentionally a hint only: the actual auth source of truth is the
 * httpOnly cookie / Bearer header. We clear the hint whenever auth
 * actually fails or on logout, so a wrong guess self-corrects within one
 * round-trip.
 */
const SESSION_HINT_KEY = "kiddo_session_hint_v1";

function readSessionHint() {
  try {
    const raw = localStorage.getItem(SESSION_HINT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSessionHint(hint) {
  try {
    if (hint) localStorage.setItem(SESSION_HINT_KEY, JSON.stringify(hint));
    else localStorage.removeItem(SESSION_HINT_KEY);
  } catch { /* private mode / quota — best-effort */ }
}

/** Synchronous read of the last-known auth hint. Used by route guards that
 *  need to decide between a loader and the marketing page during the first
 *  paint (before `refresh()` resolves). */
export function probablyAuthed() {
  // If we have a Bearer token in localStorage, the user was definitely
  // signed in last time. The hint covers the pure-cookie case where the
  // Bearer was never stored (Safari ITP / cookie-only flows).
  if (tokenStore.get()) return true;
  return readSessionHint() !== null;
}

/**
 * Auth state for Kiddo+.
 *
 * Hybrid token storage so login survives Safari ITP & incognito browsing:
 *   - Backend sets `kiddo_token` (httpOnly cookie). When the browser allows
 *     it, that's what authenticates every subsequent request.
 *   - We ALSO mirror the JWT into localStorage via `tokenStore`, and
 *     `lib/api.js` automatically adds `Authorization: Bearer <token>` on
 *     every request. The backend accepts either — whichever the browser
 *     lets through wins.
 *
 * `impersonatedBy` is the admin id when the cookie/Bearer holds an
 * `impersonate:<admin_token>:<target_uid>` payload — surfaced by the backend
 * via the `_impersonated_by` field on `/auth/me`.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonatedBy, setImpersonatedBy] = useState(null);

  // Initialise the SWR cache scope synchronously from the last-known
  // session hint so cache reads at the very first paint already land in
  // the correct user's namespace. Avoids a one-frame window where Activity
  // / Progress mount under `anon` and accidentally see no cache.
  useEffect(() => {
    const hint = readSessionHint();
    if (hint?.uid) setActiveScope(hint.uid);
  }, []);

  const refresh = async () => {
    try {
      const u = await authApi.me();
      setUser(u);
      setImpersonatedBy(u?._impersonated_by || null);
      // Update the synchronous auth hint so the next cold open of / can
      // decide between BrandedLoader and Landing without flashing.
      writeSessionHint(u ? { uid: u.id, role: u.role } : null);
      setActiveScope(u?.id || null);
      return u;
    } catch {
      setUser(null);
      setImpersonatedBy(null);
      // Cookie/Bearer is no longer valid — wipe the fallback so we don't
      // keep sending a dead token on every request.
      tokenStore.clear();
      tokenStore.clearAdminBackup();
      writeSessionHint(null);
      setActiveScope(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const login = async (email, password) => {
    const res = await authApi.login({ email, password });
    tokenStore.set(res.token);
    setUser(res.user);
    setImpersonatedBy(null);
    writeSessionHint(res.user ? { uid: res.user.id, role: res.user.role } : null);
    _relinkPush();
    return res;
  };

  const signup = async (data) => {
    const res = await authApi.signup(data);
    tokenStore.set(res.token);
    setUser(res.user);
    setImpersonatedBy(null);
    writeSessionHint(res.user ? { uid: res.user.id, role: res.user.role } : null);
    _relinkPush();
    return res;
  };

  const loginWithGoogle = async (payload) => {
    const res = await authApi.google(payload);
    tokenStore.set(res.token);
    setUser(res.user);
    setImpersonatedBy(null);
    writeSessionHint(res.user ? { uid: res.user.id, role: res.user.role } : null);
    _relinkPush();
    return res;
  };

  const logout = async () => {
    // Retrieve current push endpoint BEFORE clearing auth so the backend
    // can delete the subscription while the cookie is still valid.
    let pushEndpoint = null;
    try {
      const sub = await getCurrentSubscription();
      if (sub) pushEndpoint = sub.toJSON().endpoint;
    } catch { /* push not supported or SW not ready — ignore */ }

    try { await authApi.logout(pushEndpoint); } catch { /* server may already be gone */ }
    tokenStore.clear();
    tokenStore.clearAdminBackup();
    writeSessionHint(null);
    // Drop the in-memory + sessionStorage SWR cache so the next user that
    // signs in on this tab doesn't briefly see the previous account's data.
    try {
      const { clearCache } = await import("@/lib/cache");
      clearCache();
    } catch { /* cache module unavailable — ignore */ }
    setUser(null);
    setImpersonatedBy(null);
  };

  /** After a successful login/signup, if the browser already has push
   *  permission granted, silently re-subscribe to link this device to the
   *  new account. Handles the shared-device scenario where a previous user
   *  was subscribed — the backend cleans up the stale subscription during
   *  the subscribe call. */
  const _relinkPush = async () => {
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        await subscribePush();
      }
    } catch { /* non-critical — push re-link failures are silent */ }
  };

  const startImpersonation = async (res) => {
    // Backend already swapped the httpOnly cookies. For Bearer-header
    // browsers, stash the admin's token in a backup key and switch the
    // active token to the impersonation token so subsequent requests use it.
    if (res?.token) {
      const adminTok = tokenStore.get();
      if (adminTok && !adminTok.startsWith("impersonate:")) {
        tokenStore.setAdminBackup(adminTok);
      }
      tokenStore.set(res.token);
    }
    return refresh();
  };

  const stopImpersonation = async () => {
    try {
      const res = await adminApi.stopImpersonate();
      // Backend returns the original admin token in `token` for Bearer clients.
      // Fall back to localStorage backup if response didn't include it.
      const restored = res?.token || tokenStore.getAdminBackup();
      if (restored) {
        tokenStore.set(restored);
      } else {
        tokenStore.clear();
      }
      tokenStore.clearAdminBackup();
    } catch {
      // Backend call failed but try the local backup so admin isn't stuck
      const restored = tokenStore.getAdminBackup();
      if (restored) tokenStore.set(restored);
      tokenStore.clearAdminBackup();
    }
    return refresh();
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, signup, loginWithGoogle, logout, refresh, impersonatedBy, startImpersonation, stopImpersonation }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
